import { goBack, previousRoute, routePath, type Route } from '../platform/router.js';

/**
 * THE PICKING SCREEN'S WHOLE HEADER (0.1.396).
 *
 * ⚠️ A PICKER IS NOT A TAB (maintainer, 26 Aug: *"I want the navigation bar out of this screen to
 * really just have it as a list screen that I can select something from ... we just have the
 * navigation bar with the white band and a back button to the project detail screen or the
 * character detail screen we just came from"*).
 *
 * The picking modes are the real list screens in a mode, which is what keeps them from drifting
 * from the tabs they pick out of — but they inherited the tabs' CHROME with it: the area chip that
 * switches to another area, the counts, the waiting badge. Every one of those is a way out of an
 * errand somebody is halfway through, and taking it abandons the pick with no sign it was
 * abandoned. 0.1.395 removed the phone tab bar for the same reason; this removes the rest.
 *
 * What is left is the band itself — the same white surface strip the detail screens wear — holding
 * one control: the way back to where the errand started. The search and the filter stay BELOW the
 * band, because those are how the list is made usable, not ways to leave it.
 */
export function PickerBand({
  backTo,
  label,
  children,
  trailing,
}: {
  backTo: Route;
  label: string;
  /**
   * THE REST OF THE ONE HEADER ROW (0.1.527; maintainer, 2 Oct 2026: "the back button has a whole
   * row to itself, so move it down into the row below so that we don't have 2 rows in the header";
   * "the header is broken. the back button is in the wrong place and the search box is pushed to a
   * row below"). Every DESK picker passes its search and its "Add N to X" act here, so the back
   * chip, the search and the act share ONE row — the 0.1.397 "band is the back chip and nothing
   * else" split is retired with the second row it made. Since 0.1.528 the PHONE passes no children
   * (see `trailing`).
   *
   * ⚠️ ORDER (0.1.528; maintainer, 3 Oct 2026: "In the desk item picker screen make sure that the
   * actions are to the left of the search bar"): pass the acts FIRST — Select all · Clear selection
   * · "Add N to X" — and the search LAST. The CSS keeps DOM order and pushes the cluster right, so
   * the search ends the row, as the band search does on every tab.
   */
  children?: React.ReactNode;
  /**
   * THE BAND'S FAR-RIGHT SLOT (0.1.528; maintainer, 3 Oct 2026: "all the picker screens should also
   * have their display function ... on mobile in the header we have the back button ... and the
   * display pill button to group, sort and filter. And below the header we have the search box").
   * The phone pickers pass their `DisplayPill` here and NO children — the search drops to its own
   * row under the band, the act floats. The desk keeps the one-row `children` (search + act); its
   * Display bar is under the band, so it passes nothing here.
   *
   * With a trailing control the back chip may SHRINK (its label ellipsises) and the trailing never
   * does — a deliberate deviation from DESIGN.md's "back chip is flex: none": the band has two
   * controls here, and a long reservation name must not push the Display pill off a 375px screen.
   */
  trailing?: React.ReactNode;
}): JSX.Element {
  return (
    /**
     * ⚠️ ITS OWN ROW, NOT A CHILD OF THE SCREEN'S HEADER (0.1.397, device report). 0.1.396 put this
     * chip inside each screen's existing header container and painted THAT white — so the band
     * swallowed the search box and the Filter/Select chips with it, and the maintainer's screenshots
     * show a white slab three rows deep where one row was asked for. The band is the back chip and
     * nothing else; everything else belongs below it, on the page ground.
     */
    <div className={trailing === undefined || trailing === null ? 'picker-band' : 'picker-band picker-band-has-trailing'}>
      <a
        className="chip back-chip picker-band-back"
        href={routePath(previousRoute() ?? backTo)}
        onClick={(e) => {
          e.preventDefault();
          // A cancelled pick returns to the screen the errand started on (0.1.524, `goBack`) —
          // which is `backTo` in every walk the app offers; `backTo` is the fresh-visit fallback.
          goBack(backTo);
        }}
      >
        {/* The label in its own span so it can ellipsise — an inline-flex `<a>`'s bare text cannot. */}
        <span className="picker-band-back-label">← {label}</span>
      </a>
      {children}
      {trailing !== undefined && trailing !== null && <span className="picker-band-trailing">{trailing}</span>}
    </div>
  );
}
